import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { Guid } from "guid-typescript";
import _ from "lodash";
import React, { PureComponent } from "react";
import { KComponentFunctions } from "../../../../../shared/utilty/component-functions";
import DynamicStyleManager from "../../../../../shared/utilty/dynamic-style-manager";
import "./table.scss";

declare let window: any;

export interface TableRowProps {
  rowData?: any;
  rowIndex?: number;
  style?: any;
  hoverFontColor?: string;
  hoverBgColor?: string;
  tableClassName?: Guid;
  dynamicBackGroundColor?: string;
  calculatedRowBgColor?: (rowIndex?: number, rowData?: any) => string;
  calculatedRowFontColor?: (rowIndex?: number, rowData?: any) => string;
  onClick?: (e: any, rowIndex: number, rowData: any) => void;
  rowComponent?: any;
  showCursorPointer?: boolean;
  editMode?: boolean;
  children?: any;
  showTableHeader?: boolean;
  transformedOnMobileResolution?: boolean;
  hideHeaderOnMobileResolution?: boolean;
  selectedBgColor?: string;
  isSelected?: boolean;
  isInsertRow?: boolean;
  isInsertRowActive?: boolean;
  isEditableAlwaysActive?: boolean;
  handleAddRow?: () => void;
  handleCancelEditing?: () => void;
}

export interface TableRowState {
  uniqueKey?: Guid;
  editModeEnabled?: boolean;
  expanded?: boolean;
  isMobileResolution?: boolean;
}

export class TableRow<P, S> extends PureComponent<TableRowProps & P, TableRowState> {
  private memoizedDynamicCssResult = "";

  private styleManager: DynamicStyleManager;

  constructor(props: TableRowProps) {
    super(props as TableRowProps & P);
    const uniqueKey = Guid.create();
    this.state = {
      uniqueKey,
      editModeEnabled: false,
      expanded: false,
      isMobileResolution: window.innerWidth < 576
    } as TableRowState & S;

    this.styleManager = new DynamicStyleManager(`ktablerow_${uniqueKey.toString().substring(0, 8)}_hover`);

    this.debouncedHandleResize = _.debounce(this.handleResize, 1000).bind(this);

    window.addEventListener("resize", this.debouncedHandleResize);
  }

  private memoizedScreenWidth = 0;

  private debouncedHandleResize: () => void;

  componentDidMount() {
    this.handleResize();
    this.updateHoverCss();
  }

  componentDidUpdate(prevProps: TableRowProps & P) {
    if (
      prevProps.hoverBgColor !== this.props.hoverBgColor ||
      prevProps.hoverFontColor !== this.props.hoverFontColor ||
      prevProps.selectedBgColor !== this.props.selectedBgColor ||
      prevProps.showCursorPointer !== this.props.showCursorPointer
    ) {
      this.updateHoverCss();
    }
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.debouncedHandleResize);
    this.styleManager.removeStyle();
  }

  handleResize = () => {
    const isDesignTime = window.kuika?.isDesignTime;
    const screenWidth = window.innerWidth;
    if (screenWidth === this.memoizedScreenWidth || !this.props.transformedOnMobileResolution) return;
    this.memoizedScreenWidth = screenWidth;
    const table = this.getParentTable();
    const isMobileResolution = screenWidth < 576;
    const childrenLength = React.Children.toArray(this.props.children)?.length;

    if (screenWidth < 576 && childrenLength > 3) {
      if (table) {
        if (!table.classList.contains("kuika_table__wrapper__mobile")) {
          table.classList.add("kuika_table__wrapper__mobile");
        }
        if (this.props.hideHeaderOnMobileResolution) {
          table.classList.add("kuika_table__wrapper__mobile--witout-header");
        }
      }
    } else if (screenWidth >= 576) {
      if (table) {
        table.classList.remove("kuika_table__wrapper__mobile");
        table.classList.remove("kuika_table__wrapper__mobile--witout-header");
      }
    }

    if (this.state.isMobileResolution === isMobileResolution) return;
    this.setState({ isMobileResolution });
  };

  getUniqueClassName = (): string => {
    if (!this.state.uniqueKey) {
      return "";
    }
    return `ktablerow_${this.state.uniqueKey.toString().substring(0, 8)}`;
  };

  getClassName = (): string => {
    const uniqueClassName = this.getUniqueClassName();
    if (!uniqueClassName) {
      return "";
    }
    let result = uniqueClassName;
    const isReportDesigner = window.kuika?.dashboardState === 17;
    if (isReportDesigner) {
      result += " kuika_report-table-row";
    }
    if (this.props.isSelected) {
      result += " kuika-row-selected";
    }
    return result;
  };

  updateHoverCss = (): void => {
    const className = this.getUniqueClassName();
    if (!className) {
      return;
    }

    const isReportDesigner = window.kuika?.dashboardState === 17;
    if (isReportDesigner) {
      this.styleManager.setDynamicCss("");
      return;
    }

    const rowSelector = `.${className}`;
    const rules: string[] = [];

    const hoverDeclarations: string[] = [];
    if (this.props.hoverBgColor) {
      hoverDeclarations.push(`background-color: ${this.props.hoverBgColor} !important;`);
    }
    if (this.props.hoverFontColor) {
      hoverDeclarations.push(`color: ${this.props.hoverFontColor} !important;`);
    }
    if (hoverDeclarations.length > 0) {
      rules.push(`${rowSelector}:hover > td { ${hoverDeclarations.join(" ")} }`);
    }

    if (this.props.showCursorPointer) {
      rules.push(`${rowSelector}:hover { cursor: pointer; }`);
    }

    if (this.props.selectedBgColor) {
      rules.push(
        `${rowSelector}.kuika-row-selected:hover > td { background-color: ${this.props.selectedBgColor} !important; }`
      );
    }

    this.styleManager.setDynamicCss(rules.join("\n"));
  };

  getStyle = (): any => {
    let result: any = {};
    if (this.props.style) {
      result = _.clone(this.props.style);
    }

    if (this.props.dynamicBackGroundColor && window.kuika?.isDesignTime !== true) {
      const dataFieldKey = new RegExp(/\[datafield:([a-zA-Z0-9-_]+)\]/);
      const matchResult = this.props.dynamicBackGroundColor.match(dataFieldKey);
      if (matchResult) {
        const dataField = matchResult[1];
        const bgValue = this.props.rowData && this.props.rowData[dataField];
        if (bgValue) {
          result.backgroundColor = bgValue;
        }
      }
    }

    if (result?.display) {
      delete result.display;
    }

    return result;
  };

  handleRowClick = (e: any) => {
    if (
      this.props.rowComponent &&
      this.props.rowComponent.props.onClick &&
      this.props.rowIndex !== undefined &&
      this.props.rowData
    ) {
      if (KComponentFunctions.isButtonElement(e.target) === true) {
        return;
      }
      if (KComponentFunctions.isImgElement(e.target) === true) {
        return;
      }
      this.props.rowComponent.props.onClick(e, this.props.rowIndex, this.props.rowData);
    }
  };

  toggleExpand = () => {
    this.setState(
      (prevState: TableRowState & S) =>
        ({
          expanded: !prevState.expanded
        }) as Pick<TableRowState & S, "expanded">
    );
  };

  getProps = () => {
    const props: any = _.clone(this.props);
    delete props.children;
    return props;
  };

  getParentTable = () => {
    const parentTable = document.querySelector(`.${this.props.tableClassName}`);
    return parentTable;
  };

  handleExpandButtonClick = () => {
    this.setState({ expanded: !this.state.expanded } as Pick<TableRowState & S, "expanded">);
  };

  renderExpandButton() {
    const { expanded } = this.state;
    return (
      <div className="kuika_table__wrapper__expand-btn" onClick={this.handleExpandButtonClick}>
        {expanded ? (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M8.08516 8.12789L5.4289 10.7842C5.1411 11.072 4.67394 11.072 4.38614 10.7842C4.09834 10.4963 4.09834 10.0292 4.38614 9.74139L7.59367 6.53386C7.86548 6.26205 8.30553 6.26205 8.57664 6.53386L11.7842 9.74139C12.072 10.0292 12.072 10.4963 11.7842 10.7842C11.4964 11.072 11.0292 11.072 10.7414 10.7842L8.08516 8.12789Z"
              fill="#292C33"
            />
          </svg>
        ) : (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M8.08513 9.20211L10.7414 6.54585C11.0292 6.25805 11.4963 6.25805 11.7841 6.54585C12.072 6.83365 12.072 7.30081 11.7841 7.58861L8.57662 10.7961C8.30481 11.068 7.86476 11.068 7.59364 10.7961L4.38612 7.58861C4.09831 7.30081 4.09831 6.83365 4.38612 6.54585C4.67392 6.25805 5.14107 6.25805 5.42887 6.54585L8.08513 9.20211Z"
              fill="#292C33"
            />
          </svg>
        )}
      </div>
    );
  }

  renderMobileRow() {
    const { expanded } = this.state;
    const { children } = this.props;
    const childrenArray = React.Children.toArray(children);

    const isAnyAlwaysVisibleColumn = childrenArray.some(
      (child: any) => child?.props?.additionalProps?.alwaysVisibleOnMobileResolution === false
    );

    const isEveryColumnAlwaysVisible = childrenArray.every(
      (child: any) => child?.props?.additionalProps?.alwaysVisibleOnMobileResolution === false
    );

    let filteredChildren: any = isAnyAlwaysVisibleColumn
      ? childrenArray.filter((child: any) => child?.props?.additionalProps?.alwaysVisibleOnMobileResolution !== false)
      : childrenArray;

    if (
      filteredChildren &&
      filteredChildren.length === 1 &&
      filteredChildren[0]?.props?.className === "ant-table-selection-column"
    ) {
      filteredChildren = children;
    }

    return (
      <tr {...this.getProps()} onClick={this.handleRowClick} style={this.getStyle()} className={this.getClassName()}>
        {expanded || isEveryColumnAlwaysVisible ? children : filteredChildren}
        {isAnyAlwaysVisibleColumn && !isEveryColumnAlwaysVisible && this.renderExpandButton()}
        {this.renderIcons()}
      </tr>
    );
  }

  // Committing on mousedown (with preventDefault) keeps focus in the edited input, so the
  // cell's blur handler — which also commits the insert row — never runs for this gesture.
  // With plain onClick the blur fires first and the row would be committed twice, or, for
  // the cancel icon, committed instead of cancelled.
  handleAddRowMouseDown = (event: React.MouseEvent): void => {
    event.preventDefault();
    this.props.handleAddRow?.();
  };

  handleCancelEditingMouseDown = (event: React.MouseEvent): void => {
    event.preventDefault();
    this.props.handleCancelEditing?.();
  };

  swallowIconClick = (event: React.MouseEvent): void => {
    event.stopPropagation();
  };

  renderIcons = () => {
    if (
      !this.props.isInsertRow ||
      (!this.props.isInsertRowActive && !this.props.isEditableAlwaysActive) ||
      window.kuika?.isDesignTime
    ) {
      return null;
    }

    return (
      <span
        style={{
          display: "flex",
          justifyContent: "space-around",
          alignItems: "center",
          flexDirection: "column",
          position: "absolute",
          left: "0",
          width: "20px",
          height: "56px",
          borderRadius: "4px",
          background: "rgba(195, 195, 195, 0.3)"
        }}
        className={this.props.transformedOnMobileResolution ? "kuika_table__wrapper__icons" : ""}
      >
        <CheckOutlined
          onMouseDown={this.handleAddRowMouseDown}
          onClick={this.swallowIconClick}
          style={{ color: "green", cursor: "pointer" }}
        />
        <CloseOutlined
          onMouseDown={this.handleCancelEditingMouseDown}
          onClick={this.swallowIconClick}
          style={{ color: "red", cursor: "pointer" }}
        />
      </span>
    );
  };

  renderDesktopRow() {
    return (
      <tr {...this.getProps()} onClick={this.handleRowClick} style={this.getStyle()} className={this.getClassName()}>
        {this.props.children}
        {this.renderIcons()}
      </tr>
    );
  }

  render() {
    if (this.state.isMobileResolution && !window.kuika?.isDesignTime) {
      const childrenArray = React.Children.toArray(this.props.children);
      if (childrenArray.length > 3) {
        return this.renderMobileRow();
      }
    }
    return this.renderDesktopRow();
  }
}
